import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Histogram from '../Histogram/Histogram';

import translate from '../../libs/i18n';
import toTitleCase from '../../utils/toTitleCase';
import { histogramDefaultProps } from '../../utils/defaultProps';
import { histogramShape } from '../../utils/propTypesShapes';
import { useWindowSize } from '../../utils/useWindowSize';
import { breakpoints, colors } from '../../ui/variables';

import { HistogramGalleryStyle } from './HistogramGalleryStyle';

const HistogramGallery = ({ histogram }) => {
  const language = useSelector((state) => state.settings.language);
  const windowSize = useWindowSize();

  const histogramData = useMemo(() => {
    if (histogram) {
      const { high, low, max, min, normal } = histogram;

      const current = {
        high: high ? Object.values(high) : [],
        low: low ? Object.values(low) : [],
        maxX: max || 100,
        minX: min || 0,
        normal: normal ? Object.values(normal) : [],
      };
      const all = [...current?.low, ...current?.normal, ...current?.high];

      current.minY = all?.length ? all?.reduce((a, b) => Math.min(a, b)) : 0;
      current.maxY = all?.length ? all?.reduce((a, b) => Math.max(a, b)) : 0;

      return current;
    }

    return null;
  }, [histogram]);

  const histogramAmount = useMemo(() => {
    let current = 0;

    if (histogramData) {
      const { high, low, normal } = histogramData;

      if (high?.length) {
        current += 1;
      }

      if (low?.length) {
        current += 1;
      }

      if (normal?.length) {
        current += 1;
      }
    }

    return current;
  }, [histogramData]);

  const histogramSize = useMemo(() => {
    if (windowSize?.width < breakpoints?.ipad?.width) {
      return 'small';
    }

    if (
      windowSize?.width >= breakpoints?.ipad?.width &&
      windowSize?.width < breakpoints?.ipadLandscape?.width
    ) {
      return 'medium';
    }

    if (
      (windowSize?.width >= breakpoints?.ipadLandscape?.width &&
        windowSize?.width < breakpoints?.hd?.width) ||
      histogramAmount === 1
    ) {
      return 'large';
    }

    if (histogramAmount % 2 === 0 && histogramAmount % 3 !== 0) {
      return 'medium';
    }

    if (histogramAmount % 3 === 0 || histogramAmount >= 3) {
      return 'small';
    }

    return 'medium';
  }, [histogramAmount, windowSize]);

  return (
    <HistogramGalleryStyle className={`gallery-item-size-${histogramSize}`}>
      {histogramData?.low?.length ? (
        <div className="histogram-item low-histogram">
          <h3 className="histogram-title">
            {toTitleCase(translate('lowFemale', language))}
          </h3>
          <Histogram
            color={colors?.softRed}
            data={histogramData?.low}
            maxX={histogramData?.maxX}
            maxY={histogramData?.maxY}
            minX={histogramData?.minX}
            minY={histogramData?.minY}
            size={histogramSize}
          />
        </div>
      ) : null}
      {histogramData?.normal?.length ? (
        <div className="histogram-item normal-histogram">
          <h3 className="histogram-title">Normal</h3>
          <Histogram
            color={colors?.softBlue}
            data={histogramData?.normal}
            maxX={histogramData?.maxX}
            maxY={histogramData?.maxY}
            minX={histogramData?.minX}
            minY={histogramData?.minY}
            size={histogramSize}
          />
        </div>
      ) : null}
      {histogramData?.high?.length ? (
        <div className="histogram-item high-histogram">
          <h3 className="histogram-title">
            {toTitleCase(translate('highFemale', language))}
          </h3>
          <Histogram
            color={colors?.softYellow}
            data={histogramData?.high}
            maxX={histogramData?.maxX}
            maxY={histogramData?.maxY}
            minX={histogramData?.minX}
            minY={histogramData?.minY}
            size={histogramSize}
          />
        </div>
      ) : null}
    </HistogramGalleryStyle>
  );
};

HistogramGallery.propTypes = {
  histogram: histogramShape,
};

HistogramGallery.defaultProps = {
  histogram: histogramDefaultProps,
};

export default HistogramGallery;
